import { Clip, fuellFaktor } from './timeline.js';
/**
 * Eine Diashow aus Bildern (S23). Sascha: „Mit Dauer, Übergang und Bewegung.“
 *
 * Bilder einzeln einzufügen geht längst -- eines nach dem anderen, vier
 * Sekunden lang, ohne Übergang und ohne Bewegung. Bei zwanzig Urlaubsbildern
 * ist das zwanzigmal dieselbe Handbewegung und danach noch einmal zwanzig für
 * die Blenden. Hier steht, was aus einer Liste Bilder ein Stück Film macht.
 *
 * Herauskommen **gewöhnliche Clips** und keine neue Sorte Sache: danach lässt
 * sich jeder einzelne verschieben, kürzen, anders bewegen oder wieder
 * herausnehmen. Eine „Diashow“ als eigenes Ding auf der Zeitleiste wäre ein
 * Block, den man nur als Ganzes anfassen kann -- und der erste Wunsch wäre,
 * ihn aufzumachen.
 */
/** Wie lange ein Bild steht, wenn niemand etwas anderes sagt. */
export const DIASHOW_DAUER = 4;
/** Wie lange eine Blende dauert. */
export const DIASHOW_UEBERGANG = 0.6;
/**
 * Wie weit die Bewegung heranfährt -- acht Prozent über die ganze Standzeit.
 *
 * Mehr sieht nach Kamerafahrt aus und lenkt vom Bild ab; weniger sieht man
 * nicht. Der Wert ist die übliche Wahl für den Ken-Burns-Effekt und keine
 * Messung -- gemessen ist nur, dass das Bild dabei nie den Rand freigibt.
 */
export const DIASHOW_ZOOM = 1.08;
/**
 * Wie weit ein Bild bei diesem Massstab über den Rahmen steht, in Rahmen
 * gemessen: `x` nach links und rechts zusammen, `y` nach oben und unten.
 *
 * `scale` ist die Breite der Ebene in Rahmenbreiten (7.14). Die Höhe folgt
 * dem Seitenverhältnis des Bildes. Was über 1 hinausgeht, ist der Vorrat, aus
 * dem eine Bewegung schöpfen kann, ohne den Grund freizulegen.
 */
export function ueberstand(bild, rahmen, scale) {
    if (bild.width <= 0 || bild.height <= 0 || rahmen.width <= 0 || rahmen.height <= 0)
        return { x: 0, y: 0 };
    const hoch = (scale * rahmen.width * bild.height) / (bild.width * rahmen.height);
    return { x: Math.max(0, scale - 1), y: Math.max(0, hoch - 1) };
}
/**
 * Die Bewegung eines Bildes: zwei Haltungen, Anfang und Ende.
 *
 * Herangefahren wird immer -- nur die Richtung wechselt von Bild zu Bild
 * (heran, hinaus, heran …), damit eine lange Reihe nicht in eine Richtung
 * pumpt. Geschoben wird nur dort, wo Vorrat ist: bei dem **kleineren** der
 * beiden Massstäbe, denn was dort noch über den Rand steht, steht beim
 * grösseren erst recht darüber. Die halbe Strecke, nicht die ganze -- am
 * Anschlag zu enden sieht aus wie ein Fehler.
 */
export function bewegung(bild, rahmen, i) {
    const klein = fuellFaktor(bild, rahmen);
    const gross = klein * DIASHOW_ZOOM;
    const vorrat = ueberstand(bild, rahmen, klein);
    // Geschoben wird entlang der Achse mit mehr Vorrat; die andere bleibt in der
    // Mitte. Ein Bild, das den Rahmen genau füllt, hat auf beiden null -- dann
    // ist es eine reine Fahrt, und das ist richtig so.
    const waagerecht = vorrat.x >= vorrat.y;
    const weg = (waagerecht ? vorrat.x : vorrat.y) / 2;
    // Die Richtung wechselt ebenfalls, damit zwei Bilder nebeneinander nicht
    // dieselbe Fahrt machen.
    const hin = i % 2 === 0 ? 1 : -1;
    const von = {
        x: 0.5 + (waagerecht ? (-weg / 2) * hin : 0),
        y: 0.5 + (waagerecht ? 0 : (-weg / 2) * hin),
        scale: i % 2 === 0 ? klein : gross,
        rotate: 0,
        opacity: 1,
    };
    const bis = {
        x: 0.5 + (waagerecht ? (weg / 2) * hin : 0),
        y: 0.5 + (waagerecht ? 0 : (weg / 2) * hin),
        scale: i % 2 === 0 ? gross : klein,
        rotate: 0,
        opacity: 1,
    };
    return [von, bis];
}
/**
 * Aus einer Reihe Bilder eine Reihe Clips.
 *
 * Die Blende **überlappt**, wie überall im Programm (7.89): ein Bild fängt um
 * ihre Länge früher an als sein Vorgänger aufhört. Deshalb ist die Show
 * kürzer als `Anzahl × Dauer`, und deshalb steht jedes Bild trotzdem seine
 * volle Zeit.
 */
export function diashowPlan(bilder, wunsch) {
    if (bilder.length === 0)
        return [];
    const dauer = Math.max(0.2, wunsch.dauer ?? DIASHOW_DAUER);
    // Eine Blende, die länger ist als das halbe Bild, liesse es nie ganz stehen.
    const uebergang = Math.max(0, Math.min(wunsch.uebergang ?? DIASHOW_UEBERGANG, dauer / 2));
    const kind = wunsch.kind ?? 'crossfade';
    const ab = Math.max(0, wunsch.ab ?? 0);
    const id = wunsch.id ?? ((i) => `dia-${Date.now().toString(36)}-${i}`);
    return bilder.map((bild, i) => {
        const start = ab + i * (dauer - uebergang);
        const [von, bis] = bewegung(bild, wunsch.rahmen, i);
        // „Weich“ ist hier richtig: eine Diashow soll heranfahren und wieder zur
        // Ruhe kommen, nicht mit gleichbleibendem Tempo durchziehen (S30).
        const keyframes = wunsch.bewegung === false ? [] : [
            { at: 0, transform: von, kurve: 'weich' },
            { at: dauer, transform: bis, kurve: 'weich' },
        ];
        return Clip.parse({
            id: id(i),
            source: { kind: 'asset', assetId: bild.assetId },
            label: bild.name,
            start,
            duration: dauer,
            // Ohne Bewegung steht das Bild füllend da -- einpassen mit Balken wäre
            // auch hier das Problem und nicht die Lösung (7.14).
            transform: keyframes.length > 0 ? von : { ...von, scale: fuellFaktor(bild, wunsch.rahmen), x: 0.5, y: 0.5 },
            keyframes,
            transitionIn: i > 0 && uebergang > 0 ? { kind, duration: uebergang } : null,
        });
    });
}
/** Wie lang die fertige Show wird -- mit den Überlappungen gerechnet. */
export function diashowLaenge(anzahl, dauer = DIASHOW_DAUER, uebergang = DIASHOW_UEBERGANG) {
    if (anzahl <= 0)
        return 0;
    const u = Math.max(0, Math.min(uebergang, dauer / 2));
    return dauer + (anzahl - 1) * (dauer - u);
}
//# sourceMappingURL=diashow.js.map